import React from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';

const DateDeviationKPISettings = ({
  kpiTitle,
  onKpiTitleChange,
  mode,
  onModeChange,
  activity,
  onActivityChange,
  objectType,
  onObjectTypeChange,
  dateAttribute1,
  onDateAttribute1Change,
  dateAttribute2,
  onDateAttribute2Change,
  condition,
  onConditionChange,
  aggregation,
  onAggregationChange,
  activityTypes,
  eventAttributes,
  objectTypes,
  objectDateAttributes,
  loading = false,
  activityTypesLoading = false,
  eventAttributesLoading = false,
  objectDateAttributesLoading = false,
  onKeyPress,
  localFilters,
  updateLocalFilter,
  activeFile,
}) => {
  const showWarning = dateAttribute1 && dateAttribute2 && dateAttribute1 === dateAttribute2;
  const isObjectMode = mode === 'object';

  // Debug logging for eventAttributes
  React.useEffect(() => {
    console.log('🎯 [DateDeviationKPISettings] Received eventAttributes:', eventAttributes);
    console.log('🎯 [DateDeviationKPISettings] Current activity:', activity);
  }, [eventAttributes, activity]);

  return (
    <div className="space-y-6">
      {/* KPI Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          KPI Title *
        </label>
        <input
          type="text"
          value={kpiTitle}
          onChange={(e) => onKpiTitleChange(e.target.value)}
          onKeyPress={onKeyPress}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Delivery Delay KPI"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Custom name for your KPI widget
        </p>
      </div>

      {/* Reference Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reference Type *
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => onModeChange('event')}
            disabled={loading}
            className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
              mode === 'event'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Event
          </button>
          <button
            type="button"
            onClick={() => onModeChange('object')}
            disabled={loading}
            className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
              mode === 'object'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Object
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Compare timestamps between two event attributes of an activity, or two object attributes of an object type
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {!isObjectMode && (
          <>
            {/* Activity Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Activity *
              </label>
              <select
                value={activity}
                onChange={(e) => onActivityChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading || activityTypesLoading}
              >
                <option value="">Select Activity...</option>
                {activityTypes.map((act) => (
                  <option key={act} value={act}>
                    {act}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Filter events by this activity
              </p>
            </div>

            {/* Date Attribute 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Attribute 1 *
              </label>
              <select
                value={dateAttribute1}
                onChange={(e) => onDateAttribute1Change(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!activity || loading || eventAttributesLoading}
              >
                {!activity && <option value="">Select activity first...</option>}
                {activity && <option value="">Select Attribute...</option>}
                {eventAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {activity ? 'First date field to compare' : 'Select an activity to view attributes'}
              </p>
            </div>

            {/* Date Attribute 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Attribute 2 *
              </label>
              <select
                value={dateAttribute2}
                onChange={(e) => onDateAttribute2Change(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!activity || loading || eventAttributesLoading}
              >
                {!activity && <option value="">Select activity first...</option>}
                {activity && <option value="">Select Attribute...</option>}
                {eventAttributes.filter(attr => attr !== dateAttribute1).map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {activity ? 'Second date field to compare' : 'Select an activity to view attributes'}
              </p>
            </div>
          </>
        )}

        {isObjectMode && (
          <>
            {/* Object Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Object Type *
              </label>
              <select
                value={objectType}
                onChange={(e) => onObjectTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                <option value="">Select Object Type...</option>
                {objectTypes.map((ot) => (
                  <option key={ot} value={ot}>
                    {ot}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Object type to analyze
              </p>
            </div>

            {/* Date Attribute 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Attribute 1 *
              </label>
              <select
                value={dateAttribute1}
                onChange={(e) => onDateAttribute1Change(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!objectType || loading || objectDateAttributesLoading}
              >
                {!objectType ? (
                  <option value="">Select object type first...</option>
                ) : objectDateAttributesLoading ? (
                  <option value="">Loading attributes...</option>
                ) : objectDateAttributes.length === 0 ? (
                  <option value="">No date attributes found</option>
                ) : (
                  <option value="">Select Attribute...</option>
                )}
                {objectDateAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {objectType ? 'First date field to compare' : 'Select an object type to view attributes'}
              </p>
            </div>

            {/* Date Attribute 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Attribute 2 *
              </label>
              <select
                value={dateAttribute2}
                onChange={(e) => onDateAttribute2Change(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!objectType || loading || objectDateAttributesLoading}
              >
                {!objectType ? (
                  <option value="">Select object type first...</option>
                ) : objectDateAttributesLoading ? (
                  <option value="">Loading attributes...</option>
                ) : objectDateAttributes.length === 0 ? (
                  <option value="">No date attributes found</option>
                ) : (
                  <option value="">Select Attribute...</option>
                )}
                {objectDateAttributes.filter(attr => attr !== dateAttribute1).map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {objectType ? 'Second date field to compare' : 'Select an object type to view attributes'}
              </p>
            </div>
          </>
        )}

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Condition *
          </label>
          <select
            value={condition}
            onChange={(e) => onConditionChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="greater">Attribute 1 &gt; Attribute 2</option>
            <option value="less">Attribute 1 &lt; Attribute 2</option>
            <option value="equal">Attribute 1 = Attribute 2</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Comparison condition to filter events
          </p>
        </div>

        {/* Aggregation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aggregation *
          </label>
          <select
            value={aggregation}
            onChange={(e) => onAggregationChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="median">Median</option>
            <option value="average">Average</option>
            <option value="minimum">Minimum</option>
            <option value="maximum">Maximum</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            How to aggregate deviations
          </p>
        </div>
      </div>

      {/* Validation Messages */}
      {showWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-amber-700">
              Date attributes must be different
            </span>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Local Filter Panel */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'objectAttributes']}
      />
    </div>
  );
};

export default DateDeviationKPISettings;
